/**
 * Minimal, safe markdown renderer for retreat descriptions — a port of
 * apps/holdstyring/src/components/cms/widgets/retreat/_components/Description.tsx
 * so the external embed (<holdstyring-retreat>) renders the same text the same
 * way as the retreat's landing page on the tenant site. Admin promises
 * "**fed** og *kursiv*" on the description field; before this port the embed
 * printed the asterisks literally.
 *
 * Covers what retreat texts actually use:
 *   - paragraphs (blank line = new <p>), line-breaks inside a paragraph
 *   - **bold** / *italic*
 *   - bullet lists ("- " / "* ") and numbered lists ("1. ")
 *   - "## " / "### " headings
 *   - autolinks (http://, https://, mailto:, bare e-mails)
 *
 * NO HTML pass-through: all input is escaped before markup is added, so the
 * output is safe for dangerouslySetInnerHTML. Keep the two implementations in
 * step when extending either.
 */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function renderInline(line: string): string {
  let out = escapeHtml(line)

  // Autolink (http/https/mailto) — applied on the escaped string so the href is safe.
  out = out.replace(
    /(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="hsr-md-link">$1</a>',
  )
  out = out.replace(
    /(\bmailto:[^\s<]+|\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
    (match) => {
      const href = match.startsWith('mailto:') ? match : `mailto:${match}`
      return `<a href="${href}" class="hsr-md-link">${match}</a>`
    },
  )

  // Bold + italic. Bold first so ** is claimed before single *.
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  out = out.replace(/\*([^*]+)\*/g, '<em>$1</em>')
  return out
}

export interface MarkdownBlock {
  kind: 'p' | 'ul' | 'ol' | 'h2' | 'h3'
  html: string
}

export function parseMarkdown(md: string): MarkdownBlock[] {
  const normalized = md.replace(/\r\n/g, '\n').trim()
  if (!normalized) return []

  const lines = normalized.split('\n')
  const blocks: MarkdownBlock[] = []

  let paragraphBuffer: string[] = []
  let listBuffer: { kind: 'ul' | 'ol'; items: string[] } | null = null

  const flushParagraph = () => {
    if (paragraphBuffer.length === 0) return
    blocks.push({ kind: 'p', html: paragraphBuffer.map(renderInline).join('<br />') })
    paragraphBuffer = []
  }

  const flushList = () => {
    if (!listBuffer) return
    blocks.push({ kind: listBuffer.kind, html: listBuffer.items.map((i) => `<li>${renderInline(i)}</li>`).join('') })
    listBuffer = null
  }

  for (const raw of lines) {
    const line = raw.trimEnd()

    if (line === '') {
      flushParagraph()
      flushList()
      continue
    }

    const h2 = /^##\s+(.+)$/.exec(line)
    const h3 = /^###\s+(.+)$/.exec(line)
    if (h2 || h3) {
      flushParagraph()
      flushList()
      blocks.push({ kind: h3 ? 'h3' : 'h2', html: renderInline(h3 ? h3[1] : h2![1]) })
      continue
    }

    const ulMatch = /^[-*]\s+(.+)$/.exec(line)
    const olMatch = /^\d+\.\s+(.+)$/.exec(line)
    if (ulMatch || olMatch) {
      flushParagraph()
      const kind: 'ul' | 'ol' = ulMatch ? 'ul' : 'ol'
      if (!listBuffer || listBuffer.kind !== kind) {
        flushList()
        listBuffer = { kind, items: [] }
      }
      listBuffer.items.push(ulMatch ? ulMatch[1] : olMatch![1])
      continue
    }

    flushList()
    paragraphBuffer.push(line)
  }

  flushParagraph()
  flushList()
  return blocks
}
